import { DEFAULT_INVOICE_FONT_ID } from "@common/constants/invoiceDesign";
import { createDefaultNativeInvoicePdfFieldSettings } from "@common/helpers/applyNativeInvoicePdfFieldSettings";
import {
    DEFAULT_NATIVE_INVOICE_FOOTER,
    DEFAULT_NATIVE_INVOICE_FOOTER_ALIGN,
    NativeInvoiceFormState,
    NativeInvoiceLineRow,
} from "./nativeInvoiceFormTypes";

// A line counts as empty when it still matches the blank single-line default
// (see `defaultLine`): no catalog reference, no typed text, no non-zero
// amount/adjustment, and no coding. Ignores `id` (always generated) and the
// write-back flags (never true until a save round-trips them).
function isNativeInvoiceLineRowEmpty(line: NativeInvoiceLineRow): boolean {
    return (
        line.catalogItemId === null &&
        line.catalogItemPriceId === null &&
        line.productOrService === "" &&
        line.description === "" &&
        !line.unitAmount &&
        line.tax === 0 &&
        line.discount === 0 &&
        line.deposit === 0 &&
        line.classIds.length === 0 &&
        line.accrualAccountId === null &&
        line.receivablesAccountId === null &&
        line.billableExpenseTransactionId === null
    );
}

// Whether the invoice draft still looks like a freshly-opened, untouched
// "Create Invoice" form: no customer, no typed To/notes/terms fields, no
// design/footer/adjustment/sending customization, and every line is still
// blank. Fields with a non-blank create default (footer text/alignment, font)
// are compared against that default rather than emptiness, so a brand-new
// draft still reads "empty" while a template that overrode them does not.
// Deliberately excludes fields that are always populated regardless of user
// input (From block, invoice date, page size, tax/discount/deposit mode
// toggles) — including those would make even a brand-new draft look
// "non-empty".
//
// Used to gate the template-change override confirmation: unlike the form's
// dirty flag (which only flips once the user *edits* something), this stays
// true for a draft that already has content from a previously-applied
// template the user hasn't touched since — that draft must still warn before
// a different template overwrites it.
export function isNativeInvoiceFormStateEmpty(
    state: Pick<
        NativeInvoiceFormState,
        | "customer"
        | "toTaxId"
        | "toFullName"
        | "toCompany"
        | "poNumber"
        | "note"
        | "terms"
        | "dueDate"
        | "termType"
        | "customTerm"
        | "footer"
        | "footerAlign"
        | "showPagination"
        | "tax"
        | "discount"
        | "deposit"
        | "logoPath"
        | "backgroundColor"
        | "accentColor"
        | "textColor"
        | "fontFamily"
        | "labelOverrides"
        | "fieldSettings"
        | "sendingEmailId"
        | "collectPayment"
        | "stripeFinancialAccountId"
        | "emailTo"
        | "emailCc"
        | "emailBcc"
        | "isEmailSubjectAutomatic"
        | "isEmailMessageAutomatic"
        | "lines"
    >,
): boolean {
    return (
        state.customer === null &&
        state.toTaxId === "" &&
        state.toFullName === "" &&
        state.toCompany === "" &&
        state.poNumber === "" &&
        state.note === "" &&
        state.terms === "" &&
        state.dueDate === "" &&
        state.termType === null &&
        state.customTerm === null &&
        state.footer === DEFAULT_NATIVE_INVOICE_FOOTER &&
        state.footerAlign === DEFAULT_NATIVE_INVOICE_FOOTER_ALIGN &&
        state.showPagination &&
        state.tax === 0 &&
        state.discount === 0 &&
        state.deposit === 0 &&
        state.logoPath === null &&
        state.backgroundColor === null &&
        state.accentColor === null &&
        state.textColor === null &&
        state.fontFamily === DEFAULT_INVOICE_FONT_ID &&
        state.labelOverrides === null &&
        JSON.stringify(state.fieldSettings) ===
            JSON.stringify(createDefaultNativeInvoicePdfFieldSettings()) &&
        state.sendingEmailId === null &&
        !state.collectPayment &&
        state.stripeFinancialAccountId === null &&
        state.emailTo === "" &&
        state.emailCc === "" &&
        state.emailBcc === "" &&
        state.isEmailSubjectAutomatic &&
        state.isEmailMessageAutomatic &&
        state.lines.every(isNativeInvoiceLineRowEmpty)
    );
}
